import {
  Color3,
  Constants,
  DynamicTexture,
  PBRMaterial,
  RawCubeTexture,
  Scene,
  Texture,
} from '@babylonjs/core';
import { WaterDistancePlugin } from './WaterBumpFadePlugin.js';

const SCENE_KEY = Symbol('water-material-cache');

interface SceneCache {
  material: PBRMaterial;
  bump: Texture;
  sparkle: Texture;
  sky: RawCubeTexture;
  observer: { remove(): void };
}

/**
 * Shared water material for all water meshes in a scene. WebGPU-safe — built from
 * `PBRMaterial` (not the legacy `@babylonjs/materials` WaterMaterial which is WebGL-only).
 *
 * Why PBR (not StandardMaterial) for water:
 *
 *   StandardMaterial uses Blinn-Phong specular which has no Fresnel term. With a flat water
 *   surface, Blinn-Phong specular peaks where the half-vector between sun and view aligns
 *   with the surface normal — which mathematically maximises *directly under the camera*.
 *   You get a "headlight" blob that follows you around, no matter how low the bump strength
 *   or how tight the specular lobe.
 *
 *   PBR applies the Schlick Fresnel approximation to the GGX specular BRDF: F0 ≈ 0.02 for
 *   water at normal incidence (looking straight down — weak reflection), climbing toward
 *   ~1.0 at grazing angles (looking across to the horizon — strong, mirror-like reflection).
 *   This is exactly how real water behaves: the bright sun-glint streak forms toward the
 *   horizon in the sun's direction, fading as you look more directly down at water.
 *
 *   Direct lighting alone (the scene's directional sun) is enough to produce visible glint
 *   via GGX + Fresnel — no environment cubemap required, though one would add sky reflection
 *   in the rest of the surface.
 *
 * Surface detail:
 *
 *   - **Animated normal map** (tileable multi-octave value noise) provides ripple normals
 *     that perturb the GGX reflection direction — turns a perfect mirror streak into a
 *     scattered glittery band, like real water.
 *   - **Two scroll layers, different speeds and directions**: kills the hypnotic single-
 *     direction-slide effect of any one-layer setup. Bump scrolls slow toward +U; a sparser
 *     "sparkle" layer scrolls faster toward -V.
 *   - **Low roughness (0.08)** keeps specular tight enough to read as a real reflection.
 */
export function getWaterMaterial(scene: Scene): PBRMaterial {
  const existing = (scene as unknown as Record<symbol, SceneCache | undefined>)[SCENE_KEY];
  if (existing) return existing.material;

  const bump = makeNormalNoiseTexture(scene, 'water-bump', 256, {
    octaves: 4,
    persistence: 0.55,
    baseFreq: 3,
    // Tuned for "subtle but clearly there" — gives moving light/dark wave-shading
    // patches at close range. Higher values start to look like a sharp normal map; lower
    // values blend invisibly into the flat surface. Mipmap averaging naturally fades the
    // contribution at long distance.
    bumpStrength: 1.3,
  });
  bump.uScale = 6;
  bump.vScale = 6;

  // Sparkle layer wired into emissive. Provides the medium-range "moving water" cue.
  const sparkle = makeSparkleTexture(scene, 'water-sparkle', 256);
  sparkle.uScale = 10;
  sparkle.vScale = 10;

  // Procedural sky cubemap — the missing piece for distant water. Without an environment
  // texture, PBR has nothing to reflect, so Fresnel at grazing angles produces… nothing.
  // With this 6-face skybox (light blue at top, fog-matched at horizon), Fresnel-modulated
  // reflection naturally appears toward the distance — that's the bright horizon band you
  // see on real lakes when looking across them.
  const sky = makeSkyCubeTexture(scene, 64);

  const mat = new PBRMaterial('water-mat-shared', scene);
  // Deep blue water with a hint of green. PBR albedo is rendered as diffuse only — the
  // bright sky/sun reflections come from the specular path entirely.
  mat.albedoColor = new Color3(0.04, 0.18, 0.32);
  mat.metallic = 0;
  // Smooth surface: low roughness → tight GGX lobe → distinct sun-glint streak rather than
  // muddy shimmer everywhere. Bump map adds ripple detail on top.
  mat.roughness = 0.08;
  // Bump texture enabled — animated normal map perturbs the surface normal, producing
  // moving light/dark patches via the directional sun's diffuse term. Pure lighting
  // effect, no bright emissive colour.
  mat.bumpTexture = bump;
  // Dial down the bump's influence so the sun-glint stays a coherent streak rather than
  // being smeared into uniform shimmer (which is what high bumpStrength did before).
  mat.useParallax = false;
  // Tint the indirect reflection slightly toward a sky-blue so even without an environment
  // cubemap, distant water doesn't go pure black on the reflective side. (Without IBL these
  // factors only weakly affect the look, but they don't hurt.)
  mat.reflectivityColor = new Color3(1.0, 1.0, 1.0);

  // Sparkle re-enabled, paired with the WaterDistancePlugin fade. If the fade works
  // correctly, sparkles will be entirely absent within ~6m of the camera and ramp to
  // full strength by ~30m. If you still see bright sparkles right next to the car, the
  // plugin's shader injection isn't being honoured by the WebGPU pipeline — check the
  // console for the three diagnostic log lines from WaterDistancePlugin.
  mat.emissiveColor = new Color3(1.0, 0.95, 0.85);
  mat.emissiveTexture = sparkle;
  mat.emissiveIntensity = 1.2;
  mat.ambientColor = new Color3(0.04, 0.1, 0.18);

  // Reflection — dim Fresnel-modulated sky cubemap. PBR's Schlick Fresnel produces a
  // strong reflection at grazing angles regardless of distance (it's a function of view
  // *angle*, not distance), so to make the reflection visually appear only "far away" we
  // dim the contribution heavily — both via the cubemap colours themselves (see
  // makeSkyCubeTexture, dimmed) and via environmentIntensity here. The reflection then
  // only dominates where everything else (terrain colour, wave shading, sparkles) has
  // faded out under fog at long distance.
  mat.reflectionTexture = sky;
  mat.environmentIntensity = 0.55;

  // Per-fragment emissive (sparkle) distance fade. Bump-shaded waves stay at full
  // material-level intensity so they're visible up close; the sparkle layer is fully
  // suppressed within 20m of the camera and ramps to full strength by 100m. Wide range
  // gives a gentle build-up across the body of the water rather than a hard transition.
  new WaterDistancePlugin(mat, { emissiveFadeNear: 20, emissiveFadeFar: 100 });

  // Alpha blending so we can see a hint of submerged terrain near the shore.
  mat.alpha = 0.9;
  mat.transparencyMode = PBRMaterial.PBRMATERIAL_ALPHABLEND;
  mat.backFaceCulling = false;
  mat.separateCullingPass = true;

  // Direct-light intensity boost for the sun's specular contribution. PBR Fresnel naturally
  // suppresses near-normal reflection (under the camera) and amplifies grazing reflection
  // (toward the horizon, in the sun direction), so cranking direct light intensity makes
  // the *real* sun-glint location bright without re-introducing the under-camera blob.
  mat.directIntensity = 1.6;
  mat.environmentIntensity = 1.0;
  mat.specularIntensity = 2.2;

  // Animate both layers. Different speeds AND different angles so motion never aligns.
  // Speeds match the previous tuning the user liked for visible flow.
  const startMs = performance.now();
  const observer = scene.onBeforeRenderObservable.add(() => {
    const t = (performance.now() - startMs) * 0.001;
    bump.uOffset = (t * 0.22) % 1;
    bump.vOffset = (t * 0.13) % 1;
    sparkle.uOffset = (1 - (t * 0.35) % 1);
    sparkle.vOffset = (1 - (t * 0.58) % 1);
  });

  const cache: SceneCache = {
    material: mat,
    bump,
    sparkle,
    sky,
    observer: { remove: () => scene.onBeforeRenderObservable.remove(observer) },
  };
  (scene as unknown as Record<symbol, SceneCache | undefined>)[SCENE_KEY] = cache;

  scene.onDisposeObservable.add(() => {
    cache.observer.remove();
    bump.dispose();
    sparkle.dispose();
    sky.dispose();
    mat.dispose();
    delete (scene as unknown as Record<symbol, SceneCache | undefined>)[SCENE_KEY];
  });

  return mat;
}

// ---------------------------------------------------------------------------
// Procedural texture generation
// ---------------------------------------------------------------------------

interface NormalNoiseOpts {
  octaves: number;
  persistence: number;
  baseFreq: number;
  /** Multiplier on the encoded RG normal deflection. 0 = flat, 1 = aggressive. */
  bumpStrength: number;
}

/**
 * Multi-octave value noise rendered as a tangent-space normal map. Value noise (lattice +
 * smoothstep interpolation) doesn't have the visible periodic stripes that sum-of-sines
 * produces, so when scrolled it reads as "moving water" instead of "moving wallpaper".
 *
 * The noise is computed as a height field, then the gradient (dx, dy) is encoded into
 * R, G with B = up. Wraps cleanly because the underlying hash uses modular lattice coords.
 */
function makeNormalNoiseTexture(
  scene: Scene,
  name: string,
  size: number,
  opts: NormalNoiseOpts,
): Texture {
  const dt = new DynamicTexture(name, { width: size, height: size }, scene, false);
  const ctx = dt.getContext() as unknown as CanvasRenderingContext2D;
  const img = ctx.createImageData(size, size);
  const data = img.data;

  const heights = new Float32Array(size * size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      heights[y * size + x] = fbm2D(x / size, y / size, opts.octaves, opts.persistence, opts.baseFreq);
    }
  }

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const xR = (x + 1) % size;
      const xL = (x - 1 + size) % size;
      const yD = (y + 1) % size;
      const yU = (y - 1 + size) % size;
      const dx = heights[y * size + xR]! - heights[y * size + xL]!;
      const dy = heights[yD * size + x]! - heights[yU * size + x]!;
      const nx = -dx * opts.bumpStrength;
      const ny = -dy * opts.bumpStrength;
      const i = (y * size + x) * 4;
      data[i + 0] = Math.max(0, Math.min(255, Math.round(128 + nx * 127)));
      data[i + 1] = Math.max(0, Math.min(255, Math.round(128 + ny * 127)));
      data[i + 2] = 255;
      data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  dt.update(false);
  dt.wrapU = Texture.WRAP_ADDRESSMODE;
  dt.wrapV = Texture.WRAP_ADDRESSMODE;
  return dt;
}

/**
 * Sparkle layer — sparse high-frequency value noise, threshold-mapped so only the upper
 * tail of the noise lights up. Designed for the *emissive* channel: black baseline means
 * most fragments contribute nothing, and the bright peaks form scattered moving glints.
 *
 * Threshold 0.65 gives ~15% coverage — visible sparkles spread across the water with
 * clear black gaps in between, not a dense pattern. Combined with the WaterDistancePlugin
 * fading emissive to zero close to the camera, close-up water is solid blue and mid/far
 * water sparkles distinctly.
 */
function makeSparkleTexture(scene: Scene, name: string, size: number): Texture {
  const dt = new DynamicTexture(name, { width: size, height: size }, scene, false);
  const ctx = dt.getContext() as unknown as CanvasRenderingContext2D;
  const img = ctx.createImageData(size, size);
  const data = img.data;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const n = fbm2D(x / size, y / size, 3, 0.5, 6);
      const t = Math.max(0, (n - 0.65) / 0.35);
      const s = t * t * (3 - 2 * t); // smoothstep
      const v = Math.round(s * 255);
      const i = (y * size + x) * 4;
      data[i + 0] = v;
      data[i + 1] = v;
      data[i + 2] = v;
      data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  dt.update(false);
  dt.wrapU = Texture.WRAP_ADDRESSMODE;
  dt.wrapV = Texture.WRAP_ADDRESSMODE;
  return dt;
}

/**
 * Procedural sky cubemap for PBR reflection. Each of the 6 faces is filled with a sky
 * gradient — bright pale blue at the "top" of the visible hemisphere, darker fog-matched
 * blue at the "bottom" (horizon). The top face is a uniform pale sky.
 *
 * The exact gradient orientation per face follows the standard Babylon cube layout
 * (+X, -X, +Y, -Y, +Z, -Z). +Y is the sky's zenith. The four equatorial faces (+X, -X,
 * +Z, -Z) have the gradient running from sky at the top of the face to horizon at the
 * bottom. -Y is the bottom of the cube — rarely sampled by water reflections (which look
 * "upward" most of the time) so it's filled with horizon colour as a neutral default.
 *
 * Output is a small (64²) RGBA cubemap; PBR samples it via the reflection vector and the
 * roughness-driven mip chain. Resolution is plenty for a soft sky/horizon gradient.
 */
function makeSkyCubeTexture(scene: Scene, size: number): RawCubeTexture {
  // Dimmed sky palette — intentionally well below the scene's clear colour so that even at
  // ~100% Fresnel (very grazing angles), the reflected contribution doesn't visually
  // dominate until the only competing signal — direct view of distant water — has faded
  // to nothing under fog. Push these brighter (toward the scene's 0.62, 0.78, 0.92 clear
  // colour) if the horizon band looks too dim once fog is also factored in.
  const ZENITH: [number, number, number] = [0.32, 0.5, 0.7];
  const HORIZON: [number, number, number] = [0.5, 0.62, 0.74];
  const BOTTOM: [number, number, number] = [0.42, 0.52, 0.62];

  // Each face is sampled by direction; +Y is the top of the cube (zenith). For equatorial
  // faces, V coordinate maps top→bottom of the face image. Babylon's RawCubeTexture face
  // order: +X, -X, +Y, -Y, +Z, -Z.
  const facePlusY = makeFlatFace(size, ZENITH);
  const faceMinusY = makeFlatFace(size, BOTTOM);
  const faceEquator = makeGradientFace(size, ZENITH, HORIZON);

  const data: ArrayBufferView[] = [
    faceEquator, // +X
    faceEquator, // -X
    facePlusY, // +Y (zenith)
    faceMinusY, // -Y (nadir — rarely sampled)
    faceEquator, // +Z
    faceEquator, // -Z
  ];

  const cube = new RawCubeTexture(
    scene,
    data,
    size,
    Constants.TEXTUREFORMAT_RGBA,
    Constants.TEXTURETYPE_UNSIGNED_BYTE,
    true, // generateMipMaps — needed for PBR roughness-based blur
    false, // invertY
    Texture.TRILINEAR_SAMPLINGMODE,
  );
  // Reflection-cube coordinates so the PBR shader samples via reflected view direction.
  cube.coordinatesMode = Texture.CUBIC_MODE;
  return cube;
}

function makeFlatFace(size: number, rgb: [number, number, number]): Uint8Array {
  const buf = new Uint8Array(size * size * 4);
  const r = Math.round(rgb[0] * 255);
  const g = Math.round(rgb[1] * 255);
  const b = Math.round(rgb[2] * 255);
  for (let i = 0; i < size * size; i++) {
    buf[i * 4 + 0] = r;
    buf[i * 4 + 1] = g;
    buf[i * 4 + 2] = b;
    buf[i * 4 + 3] = 255;
  }
  return buf;
}

function makeGradientFace(
  size: number,
  top: [number, number, number],
  bottom: [number, number, number],
): Uint8Array {
  const buf = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    const t = y / (size - 1); // 0 at top to 1 at bottom
    // Smoothstep makes the horizon a soft band rather than a hard line.
    const s = t * t * (3 - 2 * t);
    const r = Math.round((top[0] + (bottom[0] - top[0]) * s) * 255);
    const g = Math.round((top[1] + (bottom[1] - top[1]) * s) * 255);
    const b = Math.round((top[2] + (bottom[2] - top[2]) * s) * 255);
    for (let x = 0; x < size; x++) {
      const i = (y * size + x) * 4;
      buf[i + 0] = r;
      buf[i + 1] = g;
      buf[i + 2] = b;
      buf[i + 3] = 255;
    }
  }
  return buf;
}

/**
 * Tileable fractional-Brownian-motion in [0, 1]. Sums `octaves` levels of value noise at
 * doubling frequencies and halving amplitudes (scaled by persistence). `baseFreq` is an
 * integer cycle count over the texture — keeps the result perfectly tileable.
 */
function fbm2D(u: number, v: number, octaves: number, persistence: number, baseFreq: number): number {
  let total = 0;
  let amplitude = 1;
  let freq = baseFreq;
  let max = 0;
  for (let o = 0; o < octaves; o++) {
    total += valueNoise2D(u * freq, v * freq, Math.round(freq)) * amplitude;
    max += amplitude;
    amplitude *= persistence;
    freq *= 2;
  }
  return total / max;
}

/**
 * Tileable value noise — bilinear interpolation of hashed lattice points with smoothstep
 * easing. `period` controls the lattice cell count so the result wraps without seams.
 */
function valueNoise2D(x: number, y: number, period: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = x - x0;
  const fy = y - y0;
  const sx = fx * fx * (3 - 2 * fx);
  const sy = fy * fy * (3 - 2 * fy);
  const v00 = hash2D(x0, y0, period);
  const v10 = hash2D(x0 + 1, y0, period);
  const v01 = hash2D(x0, y0 + 1, period);
  const v11 = hash2D(x0 + 1, y0 + 1, period);
  const ix0 = v00 + (v10 - v00) * sx;
  const ix1 = v01 + (v11 - v01) * sx;
  return ix0 + (ix1 - ix0) * sy;
}

/** Wrapping integer hash — returns a deterministic value in [0, 1) for lattice point (x, y). */
function hash2D(x: number, y: number, period: number): number {
  const xi = ((x % period) + period) % period;
  const yi = ((y % period) + period) % period;
  let h = (xi * 374761393 + yi * 668265263) | 0;
  h = ((h ^ (h >>> 13)) * 1274126177) | 0;
  h ^= h >>> 16;
  return ((h >>> 0) % 1000003) / 1000003;
}
